import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { ShieldCheck, Package, FlaskConical, Send, Twitter, Music2, Instagram } from "lucide-react";
import { fetchSocials } from "@/lib/api";
import { TrustpilotWidget } from "./TrustpilotWidget";

const SOCIAL_META = [
    { key: "telegram", label: "Telegram", icon: Send },
    { key: "twitter", label: "X / Twitter", icon: Twitter },
    { key: "tiktok", label: "TikTok", icon: Music2 },
    { key: "instagram", label: "Instagram", icon: Instagram },
];

export const Footer = () => {
    const [socials, setSocials] = useState({});
    useEffect(() => { fetchSocials().then(setSocials).catch(() => {}); }, []);
    const visibleSocials = SOCIAL_META.filter((s) => socials[s.key]);

    return (
        <footer className="bg-ink-base border-t border-ink mt-32" data-testid="site-footer">
            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-20">
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <Logo />
                        <p className="mt-6 text-sm leading-relaxed max-w-md text-ink-dim">
                            Independent research laboratory supplier. Third-party verified peptides shipped discreetly from Canada to researchers across North America.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-2">
                            {[
                                { icon: ShieldCheck, label: "Janoshik Tested" },
                                { icon: Package, label: "Discreet Shipping" },
                                { icon: FlaskConical, label: "Research Grade" },
                            ].map((b) => (
                                <div key={b.label} className="inline-flex items-center gap-2 px-3 py-1.5 border border-ink-strong overline text-[10px] text-ink-dim">
                                    <b.icon className="w-3.5 h-3.5 text-neon" strokeWidth={1.5} /> {b.label}
                                </div>
                            ))}
                        </div>

                        {visibleSocials.length > 0 && (
                            <div className="mt-8" data-testid="footer-socials">
                                <h4 className="overline text-gold mb-3">Community</h4>
                                <div className="flex flex-wrap gap-2">
                                    {visibleSocials.map((s) => (
                                        <a key={s.key} href={socials[s.key]} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-3.5 py-2 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon transition-colors text-xs font-mono uppercase tracking-wider"
                                            data-testid={`social-${s.key}`}>
                                            <s.icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {s.label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="md:col-span-2">
                        <h4 className="overline text-gold mb-5">Shop</h4>
                        <ul className="space-y-3 text-sm text-ink-dim">
                            <li><Link className="link-reveal hover:text-ink transition-colors" to="/shop?cat=GLP-1s">GLP-1s</Link></li>
                            <li><Link className="link-reveal hover:text-ink transition-colors" to="/shop?cat=Healing+Peptides">Healing Peptides</Link></li>
                            <li><Link className="link-reveal hover:text-ink transition-colors" to="/shop?cat=HGH+%26+Ancillaries">HGH & Ancillaries</Link></li>
                            <li><Link className="link-reveal hover:text-ink transition-colors" to="/shop">Catalog</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <h4 className="overline text-gold mb-5">Lab</h4>
                        <ul className="space-y-3 text-sm text-ink-dim">
                            <li><Link className="link-reveal hover:text-ink transition-colors" to="/shop">All Products</Link></li>
                            <li><Link className="link-reveal hover:text-ink transition-colors" to="/research-guidelines">Research Guidelines</Link></li>
                            <li><Link className="link-reveal hover:text-ink transition-colors" to="/track" data-testid="footer-track-link">Track My Order</Link></li>
                            <li><Link className="link-reveal hover:text-ink transition-colors" to="/support" data-testid="footer-support-link">Support · Telegram</Link></li>
                            <li><Link className="link-reveal hover:text-ink transition-colors text-neon" to="/how-to-pay-with-crypto" data-testid="footer-crypto-guide-link">How to Pay with Crypto →</Link></li>
                            <li><a className="link-reveal hover:text-ink transition-colors" href="https://janoshik.com/" target="_blank" rel="noreferrer">COA Reports</a></li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="overline text-gold mb-5">Ledger</h4>
                        <div className="space-y-2 text-xs font-mono text-ink-dim">
                            <div className="flex justify-between"><span>WAREHOUSE</span><span className="text-ink">CA / ON</span></div>
                            <div className="flex justify-between"><span>VERIFIED BY</span><span className="text-ink">JANOSHIK</span></div>
                            <div className="flex justify-between"><span>PAYMENTS</span><span className="text-ink">CRYPTO</span></div>
                            <div className="flex justify-between"><span>MIN PURITY</span><span className="text-neon">≥99%</span></div>
                        </div>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-ink">
                    {/* Trustpilot mini widget — social proof strip above the disclaimer */}
                    <div className="mb-8 flex items-center gap-4 flex-wrap" data-testid="footer-trustpilot">
                        <span className="overline text-gold text-[11px]">/ INDEPENDENT REVIEWS</span>
                        <div className="flex-1 min-w-[240px]">
                            <TrustpilotWidget variant="mini" theme="dark" height="130px" width="100%" />
                        </div>
                    </div>

                    <div className="relative p-6 border border-ink-strong bg-ink-surface corners" data-testid="disclaimer-block">
                        <p className="overline text-gold mb-3">For Research Use Only · 01</p>
                        <p className="text-xs leading-relaxed text-ink-dim">
                            All products sold by Helixora Peptides are intended <strong className="text-ink">exclusively for in-vitro research and laboratory use</strong>.
                            Products are NOT for human consumption, therapeutic use, veterinary use, food additive use, or for any form of in-vivo application.
                            Statements on this website have not been evaluated by the FDA or Health Canada.
                            By placing an order you confirm you are a qualified researcher, 21+ years of age, and that these materials will be used solely for legitimate scientific research.
                        </p>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-xs font-mono uppercase tracking-wider text-ink-mute">
                        <p>© {new Date().getFullYear()} HELIXORA.PEPTIDES · ALL RIGHTS RESERVED</p>
                        <p>SHIPS CANADA → US · CA</p>
                    </div>
                </div>
            </div>
        </footer>
    );
};
